/**
 * Shared selection + camera intent for Explore Posts list ↔ map (UX-02-A/B).
 *
 * 選取分兩層：
 * - groupKey（淺聚焦）：使用者正在看的地點群組（linkedLocation.locationId）。
 * - postId（深聚焦）：群組內被明確指定的貼文；不為 null 時必屬於 groupKey。
 */
import { toSocialMapMarker } from './socialMapModel';

export function isMapCapableItem(item) {
  return Boolean(toSocialMapMarker(item));
}

export function mapGroupKeyForItem(item) {
  const marker = toSocialMapMarker(item);
  return marker ? marker.locationId : null;
}

export function createPostsSelection({ groupKey = null, postId = null } = {}) {
  return {
    groupKey: groupKey || null,
    postId: postId != null && postId !== '' ? String(postId) : null,
  };
}

export function selectionFromMapGroup(groupKey) {
  return createPostsSelection({ groupKey });
}

export function selectionFromListItem(item) {
  const groupKey = mapGroupKeyForItem(item);
  if (!groupKey) return createPostsSelection();
  const postId = item?.postId != null ? String(item.postId) : null;
  return createPostsSelection({ groupKey, postId });
}

/** 深聚焦：只在貼文確實屬於該地點群組時成立，否則退回淺聚焦。 */
export function selectionFromPost(items, groupKey, postId) {
  const list = Array.isArray(items) ? items : [];
  const item = list.find((row) => String(row?.postId) === String(postId));
  if (item && mapGroupKeyForItem(item) === groupKey) return selectionFromListItem(item);
  return createPostsSelection({ groupKey });
}

export function discoveryKindLabelKey(kind) {
  const value = String(kind || 'ALL');
  return `travel.discover.kinds.${value}`;
}

/**
 * Camera intent for user-driven activation only.
 * 每次使用者啟用都帶新的 nonce，即使選取狀態未變也能再次對焦。
 * 座標取自該地點群組的 marker（linkedLocation 座標），不從 key 解析。
 */
export function createCameraRequestForGroup(items, groupKey, { nonce, source = 'user', postId = null } = {}) {
  if (!groupKey || nonce == null) return null;
  const list = Array.isArray(items) ? items : [];
  for (const row of list) {
    const marker = toSocialMapMarker(row);
    if (marker && marker.locationId === groupKey) {
      return {
        nonce: Number(nonce),
        latitude: marker.latitude,
        longitude: marker.longitude,
        source: String(source || 'user'),
        groupKey,
        postId: postId != null && postId !== '' ? String(postId) : null,
      };
    }
  }
  return null;
}

/** Viewport 地圖：camera 意圖取自聚焦地點快照座標（marker 集合不含貼文）。 */
export function createCameraRequestForLocation(location, { nonce, source = 'user', postId = null } = {}) {
  if (!location?.locationId || nonce == null) return null;
  if (!Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) return null;
  return {
    nonce: Number(nonce),
    latitude: location.latitude,
    longitude: location.longitude,
    source: String(source || 'user'),
    groupKey: location.locationId,
    postId: postId != null && postId !== '' ? String(postId) : null,
  };
}

/**
 * Drop selection when filtered items no longer contain the target.
 * While items are empty (loading), keep prior selection unless forceClear.
 */
export function reconcileSelectionWithItems(selection, items, { forceClear = false } = {}) {
  const current = selection || createPostsSelection();
  if (!current.groupKey && !current.postId) return createPostsSelection();
  const list = Array.isArray(items) ? items : [];
  if (!forceClear && list.length === 0) return current;

  if (current.postId) {
    const item = list.find((row) => String(row.postId) === String(current.postId));
    if (item && isMapCapableItem(item)) return selectionFromListItem(item);
  }

  if (current.groupKey) {
    const stillInGroup = list.some((row) => mapGroupKeyForItem(row) === current.groupKey);
    if (stillInGroup) return createPostsSelection({ groupKey: current.groupKey });
  }

  return createPostsSelection();
}
